import { Button, ButtonGroup, Text } from "@shopify/polaris";
import { useFormContext, useWatch } from "react-hook-form";
import CustomModal from "../CustomModal/CustomModal";
import ImageUploader from "../ImageUploader/ImageUploader";
import { FormRangeSlider } from "../FormFields";
import styles from "./ImageSettingsModal.module.css";

type ImageFit = "cover" | "contain" | "fill";

const IMAGE_FIT_OPTIONS: { label: string; value: ImageFit }[] = [
    { label: "Cover", value: "cover" },
    { label: "Contain", value: "contain" },
    { label: "Fill", value: "fill" },
];

interface ImageSettingsModalProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    size?: "small" | "large" | "fullScreen";
    urlFieldName: string;
    sizeFieldName: string;
    radiusFieldName: string;
    fitFieldName: string;
    minSize?: number;
    maxSize?: number;
    renderPreview?: () => React.ReactNode;
    previewTitle?: string;
    previewSubtitle?: string;
    previewPrice?: string;
}

const ImageSettingsModal = ({
    open,
    onClose,
    title = "Add image",
    size = "large",
    urlFieldName,
    sizeFieldName,
    radiusFieldName,
    fitFieldName,
    minSize = 30,
    maxSize = 100,
    renderPreview,
    previewTitle = "Single",
    previewSubtitle = "Standard price",
    previewPrice = "$0.00",
}: ImageSettingsModalProps) => {
    const { control, setValue } = useFormContext();
    const imgUrl = useWatch({ control, name: urlFieldName });
    const imgSize = useWatch({ control, name: sizeFieldName });
    const imgRadius = useWatch({ control, name: radiusFieldName });
    const imgFit: ImageFit = useWatch({ control, name: fitFieldName }) || "cover";

    const handleUploaderChange = (field: any, value: any) => {
        setValue(field, value, { shouldDirty: true });
    };

    return (
        <CustomModal
            open={open}
            onClose={onClose}
            title={title}
            size={size}
            primaryAction={{ content: "Done", onAction: onClose }}
        >
            <div className={styles.modalBody}>
                <div className={styles.settingsCol}>
                    <ImageUploader
                        handleInputChange={handleUploaderChange}
                        selectedTab={null}
                        img_url={imgUrl || ""}
                        keyName={urlFieldName}
                        styles={{
                            objectFit: "contain",
                            margin: "auto",
                        }}
                    />

                    <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: 8 }}>
                        <Text as="p" variant="bodyMd" fontWeight="medium">
                            Image Fit
                        </Text>
                        <ButtonGroup variant="segmented" fullWidth>
                            {IMAGE_FIT_OPTIONS.map((opt) => (
                                <Button
                                    key={opt.value}
                                    pressed={imgFit === opt.value}
                                    onClick={() => setValue(fitFieldName, opt.value, { shouldDirty: true })}
                                >
                                    {opt.label}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </div>

                    <FormRangeSlider
                        name={sizeFieldName}
                        min={minSize}
                        max={maxSize}
                        label="Size"
                        value={imgSize}
                        onChange={(val: number) => setValue(sizeFieldName, +val, { shouldDirty: true })}
                        suffixTemplate={(v: number) => `${v || 0}px`}
                    />

                    <FormRangeSlider
                        name={radiusFieldName}
                        min={0}
                        max={100}
                        label="Corner radius"
                        value={imgRadius}
                        onChange={(val: number) => setValue(radiusFieldName, +val, { shouldDirty: true })}
                        suffixTemplate={(v: number) => `${v || 0}px`}
                    />
                </div>

                <div className={styles.previewCol}>
                    <Text as="p" variant="headingSm">
                        Preview
                    </Text>
                    {renderPreview ? (
                        <div className={styles.livePreviewCard}>{renderPreview()}</div>
                    ) : (
                        <div className={styles.previewCard}>
                            <div
                                className={styles.previewImageWrapper}
                                style={{
                                    width: `${Math.max(imgSize || 0, 40)}px`,
                                    height: `${Math.max(imgSize || 0, 40)}px`,
                                    borderRadius: `${imgRadius || 0}px`,
                                }}
                            >
                                {imgUrl ? (
                                    <img
                                        src={imgUrl}
                                        alt=""
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: imgFit,
                                            borderRadius: `${imgRadius || 0}px`,
                                        }}
                                    />
                                ) : (
                                    <div className={styles.previewImagePlaceholder} />
                                )}
                            </div>
                            <div className={styles.previewText}>
                                {previewTitle && (
                                    <Text as="p" variant="headingSm">
                                        {previewTitle}
                                    </Text>
                                )}
                                {previewSubtitle && (
                                    <Text as="p" tone="subdued">
                                        {previewSubtitle}
                                    </Text>
                                )}
                            </div>
                            {previewPrice && (
                                <div className={styles.previewPrice}>
                                    <Text as="p" variant="headingSm">
                                        {previewPrice}
                                    </Text>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </CustomModal>
    );
};

export default ImageSettingsModal;
